import React, { forwardRef, useEffect, useState } from 'react';
import { TikTokSettingsView, TikTokSettingsViewProps } from './TikTokSettings.view';
import {
  PostTikTokPrivacy,
  TikTokPrivacy,
  useRefreshTikTokSocialProfileSettingsMutation
} from '../../../models/graphql';
import { triggerErrorToast } from '../../../helpers/triggerErrorToast';

export type TikTokSettingsControllerProps = {
  // extend your TikTokSettingsViewProps
  checked: TikTokSettingsViewProps['checked'];
  disabled: TikTokSettingsViewProps['disabled'];
  expanded: TikTokSettingsViewProps['expanded'];
  profileId: TikTokSettingsViewProps['profileId'];
  setExpanded: TikTokSettingsViewProps['setExpanded'];
  tikTokActionRules: TikTokSettingsViewProps['tikTokActionRules'];
  tikTokActionValue: TikTokSettingsViewProps['tikTokActionValue'];
  tikTokFormName: TikTokSettingsViewProps['tikTokFormName'];
  tikTokPromotionRules: TikTokSettingsViewProps['tikTokPromotionRules'];
  tikTokPromotionValue: TikTokSettingsViewProps['tikTokPromotionValue'];
  tikTokSettings: TikTokSettingsViewProps['tikTokSettings'];
  tikTokTargetRules: TikTokSettingsViewProps['tikTokTargetRules'];
  tikTokTargetValue: TikTokSettingsViewProps['tikTokTargetValue'];
  useFormInstance: TikTokSettingsViewProps['useFormInstance'];
  dropdownRef?: TikTokSettingsViewProps['parentDropdownRef'];
  isParentDropdownExpanded: TikTokSettingsViewProps['isParentDropdownExpanded'];
};

export const TikTokSettingsController = forwardRef<HTMLDivElement | null, TikTokSettingsControllerProps>(
  (props, ref): JSX.Element => {
    // Put All your methods and logic here
    const {
      tikTokSettings,
      tikTokActionValue,
      tikTokTargetValue,
      tikTokPromotionValue,
      tikTokFormName,
      useFormInstance
    } = props;
    const { privacy, commentDisabled, duetDisabled, stitchDisabled, maxVideoDuration: _ } = tikTokSettings;
    const [loading, setLoading] = useState<boolean>(false);
    const [refreshTikTokProfileSettingsMutation] = useRefreshTikTokSocialProfileSettingsMutation();
    // if we change privacy settings and we have impossible states, make sure we reset the select values
    useEffect(() => {
      if (!tikTokTargetValue) return;
      if (
        (privacy === TikTokPrivacy.PUBLIC && tikTokTargetValue === PostTikTokPrivacy.FOLLOWER_OF_CREATOR) ||
        (privacy === TikTokPrivacy.PRIVATE && tikTokTargetValue === PostTikTokPrivacy.PUBLIC_TO_EVERYONE)
      ) {
        useFormInstance.setValue(`${tikTokFormName}.privacyTarget`, null);
      }
    }, [privacy, tikTokFormName, tikTokTargetValue, useFormInstance]);

    // if settings have changed and we have selected value, we need to unselect it
    useEffect(() => {
      if (commentDisabled && tikTokActionValue?.includes('comment')) {
        useFormInstance.setValue(`${tikTokFormName}.comment`, false);
      }
    }, [commentDisabled, tikTokActionValue, tikTokFormName, useFormInstance]);
    useEffect(() => {
      if (duetDisabled && tikTokActionValue?.includes('duet')) {
        useFormInstance.setValue(`${tikTokFormName}.duet`, false);
      }
    }, [duetDisabled, tikTokActionValue, tikTokFormName, useFormInstance]);
    useEffect(() => {
      if (stitchDisabled && tikTokActionValue?.includes('stitch')) {
        useFormInstance.setValue(`${tikTokFormName}.stitch`, false);
      }
    }, [stitchDisabled, tikTokActionValue, tikTokFormName, useFormInstance]);

    // If we set target value to self only we need to remove promotion block
    useEffect(() => {
      if (tikTokTargetValue === PostTikTokPrivacy.SELF_ONLY && tikTokPromotionValue?.length) {
        useFormInstance.setValue(`${tikTokFormName}.self`, false);
        useFormInstance.setValue(`${tikTokFormName}.other`, false);
      }
    }, [tikTokTargetValue, tikTokFormName, tikTokPromotionValue, useFormInstance]);

    // If we set target value to self only we need to disable interactions
    useEffect(() => {
      if (tikTokTargetValue === PostTikTokPrivacy.SELF_ONLY && tikTokActionValue?.length) {
        useFormInstance.setValue(`${tikTokFormName}.comment`, false);
        useFormInstance.setValue(`${tikTokFormName}.duet`, false);
        useFormInstance.setValue(`${tikTokFormName}.stitch`, false);
      }
    }, [tikTokTargetValue, tikTokFormName, tikTokActionValue, useFormInstance]);

    const refreshTikTokProfileSettings: TikTokSettingsViewProps['refreshTikTokProfileSettings'] = id => {
      setLoading(true);
      refreshTikTokProfileSettingsMutation({ variables: { id } })
        .then(_data => {
          setLoading(false);
          // const cacheId = cache.identify({ __ref: `TikTokSocialProfile:${tikTokSettings.id}` });
          // // const cachedProfiles =
        })
        .catch(e => {
          triggerErrorToast('Refreshing TikTok profile failed')(e);
          setLoading(false);
        });
    };

    const viewProps: TikTokSettingsViewProps = {
      ...props,
      loading,
      refreshTikTokProfileSettings,
      parentDropdownRef: props.dropdownRef
    };
    return <TikTokSettingsView {...viewProps} ref={ref} />;
  }
);

TikTokSettingsController.displayName = 'TikTokSettingsController';
